<script lang="ts" setup>
import { computed, inject, ref } from 'vue';

import eventBus, { Events } from 'lib/event-bus';
import { useWangsVueConfig } from 'lib/plugins/WangsVue';
import useToast, { UseToastConfig } from 'lib/utils/baseToast.util';
import {
  exportToExcel,
  type ITable,
  normalizeTableData,
} from 'lib/utils/exportToExcel.util';
import mergePropsWithDefaults from 'lib/utils/mergePropsWithDefaults.util';

import type { ButtonDownloadProps } from './ButtonDownload.vue.d';
import { FetchListResponse } from '../datatable/DataTable.vue.d';
import Icon from '../icon/Icon.vue';
import useLoadingStore from '../loading/store';

const props = withDefaults(defineProps<ButtonDownloadProps>(), {
  tableName: 'datatable',
  multiTableNames: undefined,
  additionalTextBelowTable: undefined,
  label: undefined,
});

const { setLoading } = useLoadingStore();
const useToastConfig = inject<UseToastConfig>('useToastConfig');
const toast = useToast(useToastConfig);

const { defaultProps, locale } = useWangsVueConfig('ButtonDownload');
const config = computed(() => mergePropsWithDefaults(props, defaultProps));

const Preset = inject<Record<string, any>>('preset', {}).buttondownload;

const tables = ref<Record<string, ITable>>({});

/**
 * To make sure the same tablename not re-inserted to `tables` that'll be exported
 */
const usedTableNames = computed<string[]>(() => {
  return Object.entries(tables.value).map(([field]) => {
    return field;
  });
});

const fetchAllData = async (fetchFn): Promise<FetchListResponse['data']> => {
  const res = await fetchFn();
  if (res instanceof Error) throw res;
  return res?.data ?? { data: [], totalRecords: 0 };
};

const downloadExcel = (): void => {
  const { fileName, tableName, multiTableNames, dataConfigs } = config.value;

  if (dataConfigs?.length) handleTableConfig(dataConfigs);

  if (multiTableNames?.length) {
    // Add listener to download multi tables only when `props.multiTableNames` is defined
    eventBus.on('button-download:multi-tables', handleMultiTables);

    // To emit an event for each `tableName` to component DataTable to format the excel body
    multiTableNames.forEach((each) => {
      // If downloading multi tables, doesn't need to pass additionalTexts and showTableName here, because it was handled in function `handleMultiTables`
      const event = { fileName, tableName: each, multiTableNames };
      eventBus.emit('data-table:download', event);
    });
  } else {
    // If not downlaoding multi tables, then additionalTexts directly assigned here
    const event = {
      fileName,
      tableName,
      additionalTexts: props.additionalTextBelowTable,
    };

    eventBus.emit('data-table:download', event);
  }
};

const formatFileName = (): string => {
  return props.fileName
    .trim() // Remove extra space
    .replace(/[^a-zA-Z0-9\s]/g, '-') // Replace special characters with dash
    .replace(/\s+/g, '-') // Replace spaces with dash
    .replace(/-+/g, '-') // Replace multiple dashes with a single dash
    .replace(/-+$/, ''); // Remove trailing dash
};

const handleTableConfig = async (
  dataConfigs: ButtonDownloadProps['dataConfigs'],
): Promise<void> => {
  try {
    const exportedTables: ITable[] = await Promise.all(
      dataConfigs.map(async (option) => {
        const { columns, data, fetchFunction, tableTitle } = option;

        const excelColumns = columns.filter((col) => col.visible !== false);

        const includedColumns = excelColumns.filter((col) => !col.excluded);

        const headers = includedColumns.map(
          (col) => col.header ?? col.exportHeader ?? '-',
        );

        setLoading(true, locale.downloadingMessage);

        const { data: responseData } = fetchFunction
          ? await fetchAllData(fetchFunction)
          : { data: data };

        const excelBody = normalizeTableData(
          responseData,
          includedColumns,
          locale,
        );

        return {
          data: excelBody,
          headers,
          tableTitle,
        };
      }),
    );

    await exportToExcel({
      tables: exportedTables,
      additionalTexts: props.additionalTextBelowTable,
      fileName: formatFileName(),
    });
  } catch (error) {
    console.error('🚀 ~ handleTableConfig error:', error);
    toast.removeAllGroups();
    toast.add({
      message:
        config.value?.excelToastErrorMessage?.replace(
          '{fileName}',
          props.fileName,
        ) ?? 'Data gagal diunduh.',
      error,
      group: 'download',
    });
  } finally {
    setLoading(false);
  }
};

/**
 * Function to handle emitted event to download multi table
 * Collect all formatted table into 1 variable then if length of formatted table is equal to length of props.multiTableNames then it'll call function to export excel
 */
const handleMultiTables = async ({
  table,
}: Events['button-download:multi-tables']): Promise<void> => {
  try {
    // Attribute `tableName` moved into `table`
    const { tableName } = table;
    if (!tableName) return;

    if (!usedTableNames.value.includes(tableName)) {
      tables.value[tableName] = table;
    }

    if (usedTableNames.value.length !== props.multiTableNames?.length) return;
    eventBus.off('button-download:multi-tables', handleMultiTables); // Remove listener to avoid redundant event

    const exportedTable: ITable[] = [];

    // Insert each table to `exportedTable` in order of `props.multiTableNames` to make sure it's ordered correctly
    props.multiTableNames.forEach((each) => {
      exportedTable.push(tables.value[each] as ITable);
    });

    await exportToExcel({
      tables: exportedTable,
      additionalTexts: props.additionalTextBelowTable,
      fileName: formatFileName(),
    });
  } catch (error) {
    console.error(error);
  }
};
</script>

<template>
  <button @click="downloadExcel" v-bind="Preset.root({ props })" type="button">
    <Icon
      v-bind="Preset.icon"
      icon="download"
      info="Download"
      tooltip-pos="bottom"
    />

    <span v-if="label" class="inherit text-xs">{{ label }}</span>
  </button>
</template>
